import { useState, useRef, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { compressImage, dataUrlSizeKB } from "@/lib/image";
import { cardImageStyle } from "@/components/Card3D";
import { Send, Upload, Check, X, Loader2, Image as ImageIcon, Download, MessageSquare, Type } from "lucide-react";
import { toast } from "sonner";

function downloadImage(dataUrl, filename) {
  if (!dataUrl) { toast.error("No image"); return; }
  const a = document.createElement("a");
  a.href = dataUrl; a.download = filename || "card.jpg";
  document.body.appendChild(a); a.click(); a.remove();
}

function Bubble({ m, isAdmin }) {
  if (m.role === "system") {
    return (
      <div className="text-center my-1.5" data-testid="enhance-msg-system">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/35 bg-white/5 rounded-full px-2.5 py-1">{m.text}</span>
      </div>
    );
  }
  const mine = (isAdmin && m.role === "admin") || (!isAdmin && m.role === "player");
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`enhance-msg-${m.role}`}>
      <div className={`max-w-[78%] rounded-2xl px-3 py-2 ${mine ? "bg-fuchsia-500/20 border border-fuchsia-400/30 rounded-br-md" : "bg-white/[0.06] border border-white/10 rounded-bl-md"}`}>
        <div className="flex items-center gap-1.5 mb-0.5">
          <span className={`font-mono text-[9px] uppercase tracking-[0.2em] ${m.role === "admin" ? "text-cyan-300" : "text-fuchsia-300"}`}>{m.sender_name}</span>
        </div>
        <p className="text-[13px] text-white/85 whitespace-pre-wrap break-words">{m.text}</p>
      </div>
    </div>
  );
}

export function EnhanceTicket({ requestId, isAdmin = false, onResolved }) {
  const fileRef = useRef(null);
  const scrollRef = useRef(null);
  const [text, setText] = useState("");
  const [note, setNote] = useState("");
  const [pendingImg, setPendingImg] = useState(null);
  const [reading, setReading] = useState(false);
  const [tFields, setTFields] = useState({ title: "", lore: "", description: "" });

  const { data: ticket, refetch } = useQuery({
    queryKey: ["enhance-ticket", requestId],
    queryFn: async () => (await api.get(`/enhancements/${requestId}`)).data,
    refetchInterval: (q) => (["pending", "proposed"].includes(q.state.data?.status) ? 4000 : false),
    enabled: !!requestId,
  });

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [ticket?.messages?.length]);

  // Admin: prefill the draft editor once with the player's requested text.
  const prefilled = useRef(false);
  useEffect(() => {
    if (isAdmin && ticket?.kind === "text" && !prefilled.current && ticket?.requested) {
      const r = ticket.requested;
      if (r.title || r.lore || r.description) {
        setTFields({ title: r.title || "", lore: r.lore || "", description: r.description || "" });
        prefilled.current = true;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ticket?.kind, ticket?.requested, isAdmin]);

  const active = ticket && ["pending", "proposed"].includes(ticket.status);
  const isText = ticket?.kind === "text";
  const land = ticket?.orientation === "landscape";
  const aspect = land ? "aspect-[3.5/2.5]" : "aspect-[2.5/3.5]";

  const sendMsg = useMutation({
    mutationFn: async () => (await api.post(`/enhancements/${requestId}/message`, { text: text.trim() })).data,
    onSuccess: (d) => { setText(""); refetch(); },
    onError: (e) => toast.error(apiError(e, "Couldn't send")),
  });

  const submitImg = useMutation({
    mutationFn: async () => (await api.post(`/admin/enhancements/${requestId}/submit`, { front_image: pendingImg, note: note.trim() })).data,
    onSuccess: () => { toast.success("Version sent to the player"); setPendingImg(null); setNote(""); refetch(); },
    onError: (e) => toast.error(apiError(e, "Couldn't send version")),
  });

  const submitText = useMutation({
    mutationFn: async () => {
      const payload = { note: note.trim() };
      if (tFields.title.trim()) payload.title = tFields.title.trim();
      if (tFields.lore.trim()) payload.lore = tFields.lore;
      if (tFields.description.trim()) payload.description = tFields.description;
      return (await api.post(`/admin/enhancements/${requestId}/submit-text`, payload)).data;
    },
    onSuccess: () => { toast.success("Rewrite sent to the player"); setTFields({ title: "", lore: "", description: "" }); setNote(""); refetch(); },
    onError: (e) => toast.error(apiError(e, "Couldn't send rewrite")),
  });

  const accept = useMutation({
    mutationFn: async (submissionId) => (await api.post(`/cards/${ticket.card_id}/enhance-request/accept`, { submission_id: submissionId })).data,
    onSuccess: () => { toast.success("Applied to your card!"); refetch(); onResolved?.(); },
    onError: (e) => toast.error(apiError(e, "Couldn't apply")),
  });

  const deny = useMutation({
    mutationFn: async () => (await api.post(`/cards/${ticket.card_id}/enhance-request/deny`)).data,
    onSuccess: (d) => { toast.success(`Ticket closed — ${(d?.refund ?? 0).toLocaleString()} credits refunded`); refetch(); onResolved?.(); },
    onError: (e) => toast.error(apiError(e, "Couldn't close")),
  });

  const adminDecline = useMutation({
    mutationFn: async () => (await api.post(`/admin/enhancements/${requestId}/decline`)).data,
    onSuccess: (d) => { toast.success(`Closed — ${(d?.refund ?? 0).toLocaleString()} credits refunded`); refetch(); onResolved?.(); },
    onError: (e) => toast.error(apiError(e, "Couldn't close")),
  });

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) { toast.error("Image must be < 25MB"); return; }
    setReading(true);
    try { setPendingImg(await compressImage(file, { maxSize: 2400, quality: 0.95 })); }
    catch { toast.error("Couldn't read that image"); }
    finally { setReading(false); }
  };

  if (!ticket) return <div className="text-white/40 text-sm py-6 text-center"><Loader2 className="h-4 w-4 animate-spin inline mr-1" /> Loading ticket…</div>;

  const statusPill = ticket.status === "pending" ? "bg-amber-400/20 text-amber-300"
    : ticket.status === "proposed" ? "bg-cyan-400/20 text-cyan-300"
    : ticket.status === "fulfilled" ? "bg-emerald-400/20 text-emerald-300" : "bg-white/10 text-white/50";

  return (
    <div data-testid={`enhance-ticket-${requestId}`} className="space-y-4">
      {/* header */}
      <div className="flex items-center gap-2 flex-wrap">
        <MessageSquare className="h-4 w-4 text-fuchsia-300" />
        <span className="font-display text-base text-white tracking-[0.04em]">{ticket.card_title}</span>
        {isAdmin && <span className="font-mono text-[11px] text-white/45">by {ticket.user_name}</span>}
        <span className={`font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full ${statusPill}`}>
          {ticket.status === "proposed" ? "awaiting player" : ticket.status}
        </span>
        <span className="font-mono text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full bg-white/10 text-white/60">{isText ? "text rewrite" : "image"}</span>
        {ticket.cost != null && <span className="font-mono text-[10px] text-amber-300/80">{ticket.cost.toLocaleString()}c</span>}
      </div>

      {/* submissions / versions */}
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">
          {isText ? "Rewrites — pick one to apply" : "Versions — pick one to apply"}
        </div>

        {isText ? (
          <div className="space-y-2.5">
            {/* current text */}
            <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3" data-testid="enhance-version-current">
              <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mb-1">Current</div>
              <div className="text-sm text-white font-medium">{ticket.current_title || "—"}</div>
              {ticket.current_lore ? <div className="text-[12px] text-white/55 mt-1 whitespace-pre-wrap break-words">{ticket.current_lore}</div> : null}
              {ticket.current_description ? <div className="text-[11px] text-white/40 mt-1 whitespace-pre-wrap break-words">{ticket.current_description}</div> : null}
            </div>
            {(ticket.submissions || []).filter((s) => s.kind === "text").map((s, i) => (
              <div key={s.id} className="rounded-lg border border-emerald-400/40 bg-emerald-500/[0.04] p-3" data-testid={`enhance-version-${s.id}`}>
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-display text-[10px] text-black bg-emerald-300 rounded px-1">R{i + 1}</span>
                  {s.note && <span className="text-[10px] text-white/55">{s.note}</span>}
                </div>
                {s.fields?.title != null && <div className="text-sm text-white font-medium"><span className="text-white/40 text-[10px] uppercase tracking-wide mr-1">Name</span>{s.fields.title}</div>}
                {s.fields?.lore != null && <div className="text-[12px] text-white/70 mt-1 whitespace-pre-wrap break-words"><span className="text-white/40 text-[10px] uppercase tracking-wide mr-1">Lore</span>{s.fields.lore || "(cleared)"}</div>}
                {s.fields?.description != null && <div className="text-[11px] text-white/50 mt-1 whitespace-pre-wrap break-words"><span className="text-white/40 text-[10px] uppercase tracking-wide mr-1">Desc</span>{s.fields.description || "(cleared)"}</div>}
                {!isAdmin && active && (
                  <Button size="sm" data-testid={`enhance-accept-${s.id}`} disabled={accept.isPending} onClick={() => accept.mutate(s.id)}
                    className="mt-2 h-7 bg-emerald-400 text-black hover:bg-emerald-300 text-[11px]">
                    <Check className="h-3.5 w-3.5 mr-1" /> Use this rewrite
                  </Button>
                )}
              </div>
            ))}
            {(ticket.submissions || []).filter((s) => s.kind === "text").length === 0 && (
              <div className="text-white/35 text-[12px] px-1">{isAdmin ? "No rewrites yet — draft one below." : "Waiting for the vault to draft a rewrite…"}</div>
            )}
          </div>
        ) : (
        <div className="flex gap-3 overflow-x-auto pb-1">
          {/* current/original */}
          <div className="shrink-0 w-28" data-testid="enhance-version-current">
            <div className={`relative ${aspect} rounded-lg overflow-hidden bg-black border border-white/10 rarity-${ticket.rarity}`}>
              {ticket.current_image && <img src={ticket.current_image} alt="current" className="absolute inset-0 w-full h-full" style={{ objectFit: ticket.image_fit || "cover" }} draggable={false} />}
              <div className="foil-overlay" />
            </div>
            <div className="font-mono text-[9px] uppercase tracking-[0.2em] text-white/40 mt-1 text-center">Original</div>
          </div>

          {(ticket.submissions || []).filter((s) => s.kind !== "text").map((s, i) => (
            <div key={s.id} className="shrink-0 w-28" data-testid={`enhance-version-${s.id}`}>
              <div className={`relative ${aspect} rounded-lg overflow-hidden bg-black border border-emerald-400/40 rarity-${ticket.rarity}`}>
                <img src={s.image} alt={`v${i + 1}`} className="absolute inset-0 w-full h-full" style={{ objectFit: ticket.image_fit || "cover" }} draggable={false} />
                <div className="foil-overlay" />
                <span className="absolute top-1 left-1 font-display text-[10px] text-black bg-emerald-300 rounded px-1">V{i + 1}</span>
              </div>
              {s.note && <div className="text-[10px] text-white/55 mt-1 line-clamp-2">{s.note}</div>}
              <div className="font-mono text-[9px] text-white/30 text-center mt-0.5">{dataUrlSizeKB(s.image)}KB</div>
              {!isAdmin && active && (
                <Button size="sm" data-testid={`enhance-accept-${s.id}`} disabled={accept.isPending} onClick={() => accept.mutate(s.id)}
                  className="w-full mt-1 h-7 bg-emerald-400 text-black hover:bg-emerald-300 text-[11px]">
                  <Check className="h-3.5 w-3.5 mr-1" /> Use this
                </Button>
              )}
              {isAdmin && (
                <Button size="sm" variant="ghost" onClick={() => downloadImage(s.image, `${ticket.card_title}_v${i + 1}.jpg`)} className="w-full mt-1 h-6 text-white/40 text-[10px]">
                  <Download className="h-3 w-3 mr-1" /> Save
                </Button>
              )}
            </div>
          ))}

          {(ticket.submissions || []).filter((s) => s.kind !== "text").length === 0 && (
            <div className="shrink-0 self-center text-white/35 text-[12px] px-3">{isAdmin ? "No versions yet — upload one below." : "Waiting for the vault to post a version…"}</div>
          )}
        </div>
        )}
      </div>

      {/* admin: text rewrite editor */}
      {isAdmin && active && isText && (
        <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3 space-y-2" data-testid="enhance-admin-text-editor">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-fuchsia-300/80 flex items-center gap-1.5"><Type className="h-3.5 w-3.5" /> Draft a rewrite</div>
          <Input data-testid="enhance-text-title" value={tFields.title} onChange={(e) => setTFields((f) => ({ ...f, title: e.target.value }))} maxLength={120}
            placeholder="New name (optional)" className="h-8 bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm" />
          <Textarea data-testid="enhance-text-lore" value={tFields.lore} onChange={(e) => setTFields((f) => ({ ...f, lore: e.target.value }))} maxLength={4000} rows={3}
            placeholder="New lore (optional)" className="bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm" />
          <Textarea data-testid="enhance-text-description" value={tFields.description} onChange={(e) => setTFields((f) => ({ ...f, description: e.target.value }))} maxLength={2000} rows={2}
            placeholder="New description (optional)" className="bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm" />
          <div className="flex items-center gap-2">
            <Input data-testid="enhance-text-note" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Optional note to the player" className="h-8 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs" />
            <Button size="sm" data-testid="enhance-send-text" disabled={submitText.isPending || !(tFields.title.trim() || tFields.lore.trim() || tFields.description.trim())} onClick={() => submitText.mutate()} className="bg-emerald-400 text-black hover:bg-emerald-300 shrink-0">
              <Send className="h-3.5 w-3.5 mr-1" /> Send rewrite
            </Button>
          </div>
        </div>
      )}

      {/* admin: image uploader */}
      {isAdmin && active && !isText && (
        <div className="rounded-xl border border-fuchsia-400/20 bg-fuchsia-500/[0.04] p-3" data-testid="enhance-admin-uploader">
          <div className="flex items-center gap-2 mb-2">
            <Button size="sm" variant="outline" disabled={reading || !ticket.current_image} onClick={() => downloadImage(ticket.current_image, `${ticket.card_title}_original.jpg`)} className="border-white/15 text-white/80">
              <Download className="h-4 w-4 mr-1" /> Original
            </Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onPick} data-testid="enhance-file-input" />
            <Button size="sm" variant="outline" disabled={reading} onClick={() => fileRef.current?.click()} className="border-fuchsia-400/40 text-fuchsia-300 hover:bg-fuchsia-400/10" data-testid="enhance-upload-button">
              <Upload className="h-4 w-4 mr-1" /> {reading ? "Reading…" : pendingImg ? "Choose another" : "Add a version"}
            </Button>
            {pendingImg && (
              <div className="relative w-9 h-12 rounded overflow-hidden bg-black border border-emerald-400/40">
                <img src={pendingImg} alt="new" className="absolute inset-0 w-full h-full object-cover" />
              </div>
            )}
          </div>
          {pendingImg && (
            <div className="flex items-center gap-2">
              <Input data-testid="enhance-note-input" value={note} onChange={(e) => setNote(e.target.value)} maxLength={300} placeholder="Optional note (e.g. 'sharper face, same outfit')" className="h-8 bg-[hsl(220_18%_12%)] border-white/10 text-white text-xs" />
              <Button size="sm" data-testid="enhance-send-version" disabled={submitImg.isPending} onClick={() => submitImg.mutate()} className="bg-emerald-400 text-black hover:bg-emerald-300 shrink-0">
                <Send className="h-3.5 w-3.5 mr-1" /> Send version
              </Button>
            </div>
          )}
        </div>
      )}

      {/* chat thread */}
      <div>
        <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-white/40 mb-2">Conversation</div>
        <div ref={scrollRef} className="space-y-2 max-h-60 overflow-y-auto rounded-xl bg-black/30 border border-white/5 p-3" data-testid="enhance-chat-thread">
          {(ticket.messages || []).length === 0 ? (
            <div className="text-white/30 text-[12px] text-center py-4">No messages yet.</div>
          ) : (
            (ticket.messages || []).map((m) => <Bubble key={m.id} m={m} isAdmin={isAdmin} />)
          )}
        </div>
        {active && (
          <div className="flex items-center gap-2 mt-2">
            <Input
              data-testid="enhance-message-input"
              value={text}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && text.trim()) sendMsg.mutate(); }}
              maxLength={1000}
              placeholder={isAdmin ? "Reply to the collector…" : "Message the vault…"}
              className="bg-[hsl(220_18%_12%)] border-white/10 text-white text-sm"
            />
            <Button data-testid="enhance-message-send" disabled={!text.trim() || sendMsg.isPending} onClick={() => sendMsg.mutate()} className="bg-fuchsia-500 text-white hover:bg-fuchsia-400 shrink-0">
              <Send className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {/* close actions */}
      {active && (
        <div className="flex justify-end">
          {isAdmin ? (
            <Button size="sm" variant="ghost" data-testid="enhance-admin-decline" disabled={adminDecline.isPending} onClick={() => adminDecline.mutate()} className="text-white/40 hover:text-red-300">
              <X className="h-4 w-4 mr-1" /> Close & refund
            </Button>
          ) : (
            <Button size="sm" variant="ghost" data-testid="enhance-deny-button" disabled={deny.isPending} onClick={() => deny.mutate()} className="text-white/40 hover:text-red-300">
              <X className="h-4 w-4 mr-1" /> Close & refund {(ticket.cost ?? 0).toLocaleString()}
            </Button>
          )}
        </div>
      )}
      {ticket.status === "fulfilled" && <p className="text-emerald-300 text-sm flex items-center gap-1.5"><Check className="h-4 w-4" /> A version was applied to the card.</p>}
      {ticket.status === "declined" && <p className="text-white/40 text-sm">This ticket was closed and refunded.</p>}
    </div>
  );
}
